import { useIntersection } from '@mantine/hooks';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ShareIcon from '@mui/icons-material/Share';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DragModal } from 'components/DragModal/DragModal';
import { IntersectionLoader } from 'components/generic/IntersectionLoader';
import { useCosmos } from 'providers/contexts/CosmosContext';
import {
  GetSavedSearchesParams,
  GetSavedSearchesResult,
  SavedSearchItem,
} from 'providers/contexts/extensions/SavedSearches';
import { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDataProvider, useResourceContext } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { useSearchParams } from 'react-router-dom';
import { mergeDeep } from 'remeda';
import { bloxValues } from 'themes/constants';
import { ApiErrorResponse, BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { getKnownSearchQueryParams } from 'components/Search/functions';
import { useSavedSearch } from './SavedSearchContextProvider';
import { savedSearchPrivacyStyle } from './SavedSearchStyles';

// todo: future - when we start adding in saved searches support
// to other screens, this modal should take in more props to
// setup the "saveSearchPayload" so it is more generic
// and configure similalry to SavedSearchSelector props?

type SavedSearchModalProps = {
  /**
   * Payload for the saved search list query.
   * The string of `query.app.[0]` will be used
   * when saving the the saved search payload put.
   */
  addPayload?: GetSavedSearchesParams;
  /**
   * Specify the payload to use for the saved search
   * as we could be in a modal context, or url based context.
   */
  currentFilters?: Record<string, any>;
};

export const SavedSearchModal = memo(
  ({ addPayload, currentFilters }: SavedSearchModalProps) => {
    const resource = useResourceContext();
    const theme = useTheme();
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();
    const { queryClient } = useCosmosQueryClient();
    const { userInfo } = useCosmos();
    const { t } = useTranslation();
    const { savedSearchOpen, setSavedSearchOpen } = useSavedSearch();
    const [queryParams] = useSearchParams();

    const [action, setAction] = useState<string>('create');
    const [view, setView] = useState<string>('menu');
    const [searchID, setSearchID] = useState<string>('');
    const [isError, setError] = useState({ status: false, message: '' });
    const [nameError, setNameError] = useState<string>('');

    const app = useMemo(() => {
      const appProp = Array.isArray(addPayload?.query?.app)
        ? addPayload.query.app[0]
        : addPayload?.query?.app;
      return appProp;
    }, [addPayload?.query?.app]);

    // The payload that will be sent to create/update the saved search
    // Initialized with default values and updated based on user input
    const [saveSearchPayload, setSaveSearchPayload] = useState<SavedSearchItem>(() => {
      // Use currentFilters if provided (modal with local state), otherwise extract from URL
      const sourceFilters = currentFilters
        ? currentFilters
        : getKnownSearchQueryParams({
            queryParams: queryParams,
            resource: resource,
          });

      // Map channel to channel_id since getKnownSearchQueryParams stores channel_id as channel
      const { channel, ...restParams } = sourceFilters;
      const mappedParams = {
        ...restParams,
        ...(channel && { channel_id: channel }),
        // Handle special field mappings
        start_time_from: sourceFilters['start_time[min]'] || '',
        start_time_to: sourceFilters['start_time[max]'] || '',
        print_slug_slug: sourceFilters['print_slug'] || '',
        // Convert deleted to number for NXT API compatibility
        deleted: sourceFilters.deleted ? Number(sourceFilters.deleted) : '',
      };

      return {
        app: app,
        name: '',
        payload: mappedParams,
        shared: 'private',
        default: false,
      };
    });

    // Intersection observer for infinite scroll
    const { ref: ixRef, entry } = useIntersection({ root: null, threshold: 0.5 });

    const propsParams: GetSavedSearchesParams = useMemo(() => {
      return addPayload ?? {};
    }, [addPayload]);

    // Saved searches owned by the current user
    const savedSearchesQuery = useInfiniteQuery<GetSavedSearchesResult, ApiErrorResponse>(
      {
        queryKey: ['getSavedSearches', app, propsParams, userInfo?.uuid],
        queryFn: async ({ pageParam = '' }) => {
          // Default query params page/limit and scope to current user
          const defaultQuery: GetSavedSearchesParams = {
            query: {
              page: pageParam as string,
              limit: 99,
              owner: userInfo?.uuid,
            },
          };
          // Merge in with props payload params
          const payload = mergeDeep(defaultQuery, propsParams);
          const result = await dataProvider.getSavedSearches(payload);
          return result;
        },
        getNextPageParam: (lastPage) => lastPage?.data?.next,
        enabled: Boolean(userInfo?.uuid),
        gcTime: 0,
        staleTime: 0,
        retry: false,
        initialPageParam: '',
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
      },
    );

    const {
      data: mySavedItems,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
    } = savedSearchesQuery;

    // flatten all items from the infinite query
    const allItems = useMemo<SavedSearchItem[]>(() => {
      if (!mySavedItems) return [];
      const flatData =
        mySavedItems?.pages?.flatMap((page) => page?.data?.items ?? []) ?? [];
      return flatData;
    }, [mySavedItems]);

    // Translation strings
    const strError = t('blox.general.error');
    const strPrivacy = t('blox.general.privacy');
    const strPrivate = t('blox.general.private');
    const strShareable = t('blox.general.shareable');
    const strCancel = t('blox.actions.cancel');
    const strNext = t('blox.actions.next');
    const strBack = t('blox.actions.back');
    const strQuickFilters = t('blox.search.saved.quick_filters');
    const strNewQuickFilter = t('blox.search.saved.new_quick_filter');
    const strNewQuickFilterDesc = t('blox.search.saved.new_quick_filter_desc');
    const strReplaceQuickFilter = t('blox.search.saved.replace_quick_filter');
    const strReplaceQuickFilterDesc = t('blox.search.saved.replace_quick_filter_desc');
    const strQuickFilterName = t('blox.search.saved.quick_filter_name');
    const strForPrivateUse = t('blox.search.saved.for_working_privately');
    const strForSharedUse = t('blox.search.saved.for_share_with_org');
    const strSaveQuickFilter = t('blox.search.saved.save_quick_filter');

    // Callbacks and Handlers
    const closeSnackbar = useCallback(() => {
      setError((prevData) => ({
        status: false,
        message: prevData.message,
      }));
    }, []);

    const handleReplaceChange = (event: SelectChangeEvent) => {
      const searchId = event.target.value as string;
      const selectedSearch = allItems?.find((search) => search?.id === searchId);

      setSearchID(searchId);
      setSaveSearchPayload((prevState) => ({
        ...prevState,
        name: selectedSearch?.name || '',
        shared: selectedSearch?.shared ? 'public' : 'private',
      }));
    };

    const handleActionChange = (event: SelectChangeEvent) => {
      setAction(event.target.value as string);
    };

    const handleViewChange = () => {
      setView(action);
    };

    const handlePrivacyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSaveSearchPayload((prevState) => ({
        ...prevState,
        shared: event.target.value,
      }));
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSaveSearchPayload((prevState) => ({
        ...prevState,
        name: event.target.value,
      }));
    };

    const handleClose = useCallback(() => {
      setView('menu');
      setAction('create');
      setSavedSearchOpen(false);
    }, [setSavedSearchOpen]);

    const handleGetUser = useCallback(
      async (id: string) => {
        if (!id) return;
        try {
          const { data } = await dataProvider.getUserAccountSummaryById(id);
          return data;
        } catch (error) {
          console.error('Error getting user:', error);
          // Handle the error as needed
        }
      },
      [dataProvider],
    );

    const handleSearchSave = useCallback(async () => {
      // Check if the title is not empty
      if (saveSearchPayload?.name?.trim() === '') {
        // Update the title error state
        setNameError('Name cannot be empty');
        return;
      }

      // If the title is not empty, reset the title error state
      setNameError('');

      try {
        const author = queryParams.get('author') || '';
        const user = author ? await handleGetUser(author) : null;
        const updatedSaveSearchPayload = {
          ...saveSearchPayload,
          payload: {
            ...saveSearchPayload.payload,
            ...(user
              ? {
                  author_uuid: user.uuid,
                  author_screenName: user.screen_name,
                }
              : {}),
          },
          shared: saveSearchPayload.shared === 'private' ? false : true,
        };

        if (searchID) {
          await dataProvider.updateSavedSearch({
            path: { id: searchID },
            body: updatedSaveSearchPayload,
          });
        } else {
          await dataProvider.createSavedSearch({
            body: updatedSaveSearchPayload,
          });
        }

        // invalidate savedsearch queries to refresh
        // react-admin lists, modal items, quick filter selector, etc.
        await queryClient.invalidateQueries({ queryKey: ['savedsearch'] });
        await queryClient.invalidateQueries({ queryKey: ['getSavedSearches'] });

        // Close the modal after saving
        setView('menu');
        handleClose();
      } catch (error) {
        const castError = error as ApiErrorResponse;
        console.error('[handleSearchSave] Error:', castError);
        const errMsg = castError?.message ?? castError?.axiosMessage ?? strError;
        setError({
          status: true,
          message: String(errMsg),
        });
      }
    }, [
      queryClient,
      dataProvider,
      saveSearchPayload,
      searchID,
      queryParams,
      handleGetUser,
      handleClose,
      strError,
    ]);

    const modalTitle = useMemo(() => {
      switch (view) {
        case 'menu':
          return strQuickFilters;
        case 'create':
          return strNewQuickFilter;
        case 'update':
          return strReplaceQuickFilter;
        default:
          return strQuickFilters;
      }
    }, [view, strQuickFilters, strNewQuickFilter, strReplaceQuickFilter]);

    useEffect(() => {
      if (savedSearchOpen) {
        // Use currentFilters if provided (modal with local state), otherwise extract from URL
        const sourceFilters = currentFilters
          ? currentFilters
          : getKnownSearchQueryParams({
              queryParams: queryParams,
              resource: resource,
            });

        // Map channel to channel_id since getKnownSearchQueryParams stores channel_id as channel
        const { channel, ...restParams } = sourceFilters;
        const mappedParams = {
          ...restParams,
          ...(channel && { channel_id: channel }),
          // Handle special field mappings
          start_time_from: sourceFilters['start_time[min]'] || '',
          start_time_to: sourceFilters['start_time[max]'] || '',
          print_slug_slug: sourceFilters['print_slug'] || '',
          // Convert deleted to number for NXT API compatibility
          // Note: API schema is currently in flux here. Watch for changes in required type.
          deleted: sourceFilters.deleted ? Number(sourceFilters.deleted) : '',
        };

        setSaveSearchPayload((prevState) => ({
          ...prevState,
          payload: mappedParams,
          shared: 'private',
          default: false,
        }));
      }
    }, [queryParams, resource, savedSearchOpen, currentFilters]);

    return (
      <DragModal
        open={savedSearchOpen}
        preventBackdropClickClose={true}
        preventEscapeKeyClose={true}
        onClose={handleClose}
        isDraggable={false}
        isResizable={false}
        isFullscreenable={false}
        initialWidth={600}
        initialHeight={350}
        header={
          <Typography
            data-testid="saved-search-modal-title"
            sx={{
              fontSize: bloxValues.xlFontSizeRem,
              fontWeight: bloxValues.fontWeightMedium,
              color: theme.palette.text.primary,
            }}
          >
            {modalTitle}
          </Typography>
        }
        body={
          <Box
            data-testid="saved-search-modal-body"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              color: theme.palette.text.primary,
            }}
          >
            <Snackbar
              data-testid="saved-search-modal-error-snackbar"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              open={isError.status}
              autoHideDuration={5000}
              onClose={() => closeSnackbar()}
            >
              <Alert
                variant="filled"
                severity="error"
                sx={{ width: '100%' }}
              >
                <b>{strError}</b> {isError?.message}
              </Alert>
            </Snackbar>

            <FormControl
              fullWidth
              variant="outlined"
            >
              {view === 'menu' ? (
                <RadioGroup
                  name="saved-search-modal-action"
                  data-testid="saved-search-modal-radio-group"
                  aria-labelledby="saved-search-modal-action"
                  value={action}
                  onChange={handleActionChange}
                  sx={{
                    gap: 2,
                    '& .MuiFormControlLabel-label': {
                      fontWeight: bloxValues.fontWeightMedium,
                    },
                  }}
                >
                  <Box>
                    <FormControlLabel
                      data-testid="saved-search-modal-action-create"
                      value="create"
                      control={<Radio size="small" />}
                      label={strNewQuickFilter}
                    />
                    <FormHelperText>{strNewQuickFilterDesc} </FormHelperText>
                  </Box>
                  <Box>
                    <FormControlLabel
                      data-testid="saved-search-modal-action-replace"
                      value="update"
                      control={<Radio size="small" />}
                      label={strReplaceQuickFilter}
                    />
                    <FormHelperText>{strReplaceQuickFilterDesc}</FormHelperText>
                  </Box>
                </RadioGroup>
              ) : (
                <Fragment>
                  {view === 'create' && (
                    <TextField
                      data-testid="saved-search-modal-name-input"
                      required={true}
                      label={strQuickFilterName}
                      variant="outlined"
                      error={Boolean(nameError)}
                      helperText={nameError}
                      sx={{ mb: 2 }}
                      onChange={handleSearchChange}
                      autoComplete="off"
                    />
                  )}

                  {view === 'update' && (
                    <Fragment>
                      <InputLabel id="saved-search-modal-select-label">
                        {strQuickFilterName}
                      </InputLabel>
                      <Select
                        labelId="saved-search-modal-select-label"
                        data-testid="saved-search-modal-select"
                        value={searchID}
                        label={strQuickFilterName}
                        onChange={handleReplaceChange}
                        sx={{
                          mb: 2,
                        }}
                        MenuProps={{
                          slotProps: {
                            paper: {
                              sx: {
                                maxHeight: 350,
                                maxWidth: 350,
                              },
                            },
                          },
                        }}
                      >
                        {allItems.map((item, idx) => {
                          const itemId = item?.id ?? '';
                          const itemName = item?.name ?? '';
                          const isLastItem = Boolean(idx === allItems?.length - 1);

                          // if id missing dont show item
                          if (!itemId) {
                            return null;
                          }

                          // todo: refactor old input here to autocomplete
                          // with proper load more observer setup...
                          // note setup observer here is a kludge and should
                          // not be reused elsewhere, just a temp fix until
                          // autocomplete refactor as we do in other placess
                          if (isLastItem) {
                            return (
                              <MenuItem
                                key={itemId}
                                value={itemId}
                                title={itemName}
                                data-testid={`saved-search-modal-menuitem-${itemId}`}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                }}
                              >
                                {itemName}
                                <IntersectionLoader
                                  ixRef={ixRef}
                                  entry={entry}
                                  hasNextPage={hasNextPage}
                                  isFetching={isFetchingNextPage}
                                  fetchNextPage={fetchNextPage}
                                  compact={true}
                                  hideOnPagesEnd={true}
                                />
                              </MenuItem>
                            );
                          }
                          // regular render item
                          return (
                            <MenuItem
                              key={itemId}
                              value={itemId}
                              title={itemName}
                              data-testid={`saved-search-modal-menuitem-${itemId}`}
                            >
                              {itemName}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </Fragment>
                  )}
                  <FormLabel id="saved-search-modal-privacy-label">
                    {strPrivacy}
                  </FormLabel>
                  <RadioGroup
                    row={true}
                    aria-labelledby="saved-search-modal-privacy"
                    value={saveSearchPayload.shared}
                    name="saved-search-modal-privacy"
                    sx={savedSearchPrivacyStyle}
                    onChange={handlePrivacyChange}
                  >
                    <FormControlLabel
                      value="private"
                      data-testid="saved-search-modal-privacy-private"
                      control={
                        <Radio
                          size="small"
                          disabled={view === 'update' && !searchID}
                        />
                      }
                      label={
                        <Fragment>
                          <LockOutlinedIcon /> {strPrivate}
                        </Fragment>
                      }
                    />
                    <FormControlLabel
                      value="public"
                      data-testid="saved-search-modal-privacy-public"
                      control={
                        <Radio
                          size="small"
                          disabled={view === 'update' && !searchID}
                        />
                      }
                      label={
                        <Fragment>
                          <ShareIcon /> {strShareable}
                        </Fragment>
                      }
                    />
                  </RadioGroup>
                  {view === 'create' && (
                    <FormHelperText>
                      {saveSearchPayload.shared === 'private'
                        ? strForPrivateUse
                        : strForSharedUse}
                    </FormHelperText>
                  )}
                </Fragment>
              )}
            </FormControl>
          </Box>
        }
        footer={
          <Box
            data-testid="saved-search-modal-footer"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
              width: '100%',
              gap: 2,
            }}
          >
            {view === 'menu' ? (
              <Fragment>
                <Button
                  disableElevation
                  data-testid="saved-search-modal-close-button"
                  onClick={handleClose}
                >
                  {strCancel}
                </Button>
                <Button
                  disableElevation
                  variant="contained"
                  color="primary"
                  data-testid="saved-search-modal-save-button"
                  onClick={handleViewChange}
                >
                  {strNext}
                </Button>
              </Fragment>
            ) : (
              <Fragment>
                <Button
                  disableElevation
                  data-testid="saved-search-modal-back-button"
                  onClick={() => setView('menu')}
                >
                  {strBack}
                </Button>
                <Button
                  disableElevation
                  data-testid="saved-search-modal-save-button"
                  variant="contained"
                  color="primary"
                  onClick={handleSearchSave}
                >
                  {strSaveQuickFilter}
                </Button>
              </Fragment>
            )}
          </Box>
        }
      />
    );
  },
);

SavedSearchModal.displayName = 'SavedSearchModal';
